import { inject, Injectable } from '@angular/core';
import { ColorMetadataService } from '@ddv/charts';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import {
    AppWidgetState,
    ChartConfigItem,
    Fund,
    SlicerConfigItem,
    WidgetLookup,
    WidgetSnapshot,
} from '@ddv/models';
import { FundsService } from '@ddv/reference-data';
import { combineLatest, map, Observable } from 'rxjs';

import { convertToCreateWidgetRequest } from '../utils/convert-to-create-widget-request';
import { convertToUpdateWidgetRequest } from '../utils/convert-to-update-widget-request';

@Injectable()
export class WidgetsService {
    private readonly ddvApiService = inject<ApiExecutorService>(ApiServices.ddvApi);
    private readonly fundsService = inject(FundsService);

    fetchAllWidgets(clientCode: string): Observable<WidgetSnapshot[]> {
        return this.ddvApiService.invokeServiceWithParams(clientCode, 'widgets');
    }

    fetchAllWidgetLookups(clientCode: string): Observable<WidgetLookup[]> {
        return this.ddvApiService.invokeServiceWithParams(clientCode, 'widgets', { lookup: true });
    }

    validateWidgetName(name: string, clientCode: string): Observable<boolean> {
        return this.ddvApiService
            .invokeServiceWithParams<{ valid: boolean }>(clientCode, 'widgets/is-name-valid', { name: encodeURIComponent(name) })
            .pipe(map((response) => response.valid));
    }

    fetchWidgetInfo(id: number, isMulticlient: boolean, clientCode: string): Observable<AppWidgetState> {
        const widgetRequest = this.ddvApiService.invokeServiceWithParams<Partial<AppWidgetState>>(clientCode, `widgets/${id}`);

        if (isMulticlient) {
            return widgetRequest.pipe(map((result) => this.mapWidgetState(result, [])));
        }
        return combineLatest([widgetRequest, this.fundsService.funds()])
            .pipe(map(([result, funds]) => {
                return this.mapWidgetState(result, funds);
            }));
    }

    createCoreWidget(widget: AppWidgetState, isMultiClient: boolean, clientCode: string): Observable<AppWidgetState> {
        const payload = convertToCreateWidgetRequest(widget);
        const widgetRequest = this.ddvApiService.invokeServiceWithBody<Partial<AppWidgetState>>(clientCode, 'widgets', 'POST', payload);

        if (isMultiClient) {
            return widgetRequest.pipe(map((result) => new AppWidgetState(result)));
        }

        return combineLatest([widgetRequest, this.fundsService.funds()]).pipe(map(([result, funds]) => new AppWidgetState(result, funds)));
    }

    updateCoreWidget(config: AppWidgetState, isMultiClient: boolean, clientCode: string): Observable<AppWidgetState> {
        const payload = convertToUpdateWidgetRequest(config);
        const widgetRequest = this.ddvApiService.invokeServiceWithBody<Partial<AppWidgetState>>(clientCode, `widgets/${config.id}`, 'PUT', payload);

        if (isMultiClient) {
            return widgetRequest.pipe(map((widget) => new AppWidgetState(widget)));
        }

        return combineLatest([widgetRequest, this.fundsService.funds()]).pipe(map(([widget, funds]) => new AppWidgetState(widget, funds)));
    }

    deleteWidget(widgetId: number, clientCode: string): Observable<void> {
        return this.ddvApiService.invokeServiceWithBody(clientCode, `widgets/${widgetId}`, 'DELETE', null);
    }

    // The mapping in the methods below should be done in the API and they should be removed afterwards
    private mapWidgetState(widget: Partial<AppWidgetState>, funds: Fund[]): AppWidgetState {
        if (!widget.visualizationConfigs) {
            return new AppWidgetState(widget, funds);
        }

        widget.visualizationConfigs.forEach((vizConfig) => {
            if (vizConfig.visualizationType === 'LINE_CHART') {
                if (vizConfig.configs?.slicers?.length) {
                    this.mapLineChartConfigCustomStyles(vizConfig.configs.slicers);
                } else if (vizConfig.configs?.values?.length) {
                    this.mapLineChartConfigValueColorNames(vizConfig.configs.values as ChartConfigItem[]);
                }
            }
        });
        return new AppWidgetState(widget, funds);
    }

    private mapLineChartConfigCustomStyles(values: SlicerConfigItem[]): void {
        values.forEach((v) => {
            v.configCustomStyles = v.configCustomStyles?.filter((attribute) => {
                return !ColorMetadataService.isLineChartConfigCustomStyleOutdated(attribute.attributeColorName);
            });
        });
    }

    private mapLineChartConfigValueColorNames(values: ChartConfigItem[]): void {
        values.forEach((value, index) => {
            value.colorName = ColorMetadataService.mapOutdatedLineChartValueColorName(value.colorName ?? '', index);
        });
    }
}
